import { API_BASE_URL } from '../config/constants';

export interface BackofficeUser {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  role: 'super_admin' | 'backoffice_admin' | 'backoffice_user';
  is_active: boolean;
  is_verified: boolean;
  last_login_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateBackofficeUserRequest {
  email: string;
  password: string;
  full_name: string;
  role: 'super_admin' | 'backoffice_admin' | 'backoffice_user';
}

export interface UpdateBackofficeUserRequest {
  full_name?: string;
  role?: 'super_admin' | 'backoffice_admin' | 'backoffice_user';
  is_active?: boolean;
}

export interface BackofficeUserListResponse {
  users: BackofficeUser[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface AvailableRole {
  value: string;
  label: string;
}

export interface AvailableRolesResponse {
  roles: AvailableRole[];
}

class BackofficeUserService {
  private getAuthHeaders() {
    const token = localStorage.getItem('backoffice_access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async getUsers(
    page: number = 1,
    size: number = 20,
    search?: string,
    role?: string,
    is_active?: boolean
  ): Promise<BackofficeUserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (role) params.append('role', role);
    if (is_active !== undefined) params.append('is_active', is_active.toString());

    const response = await fetch(
      `${API_BASE_URL}/backoffice/users?${params.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch users');
    }

    return response.json();
  }

  async getUser(userId: string): Promise<BackofficeUser> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/${userId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch user');
    }

    return response.json();
  }

  async createUser(userData: CreateBackofficeUserRequest): Promise<BackofficeUser> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create user');
    }

    return response.json();
  }

  async updateUser(userId: string, userData: UpdateBackofficeUserRequest): Promise<BackofficeUser> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/${userId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update user');
    }

    return response.json();
  }

  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete user');
    }
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/${userId}/reset-password`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reset password');
    }
  }

  async changeOwnPassword(passwordData: ChangePasswordRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/change-password`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change password');
    }
  }

  async getAvailableRoles(): Promise<AvailableRolesResponse> {
    const response = await fetch(`${API_BASE_URL}/backoffice/users/roles/available`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch available roles');
    }

    return response.json();
  }
}

export const backofficeUserService = new BackofficeUserService();